import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import "../styles/Jobs.css";

const emptyForm = {
  title: "",
  peopleWanted: 1,
  category: "",
  salary: "",
  jobType: "Full Time",
  location: "",
  description: "",
  workingHours: "",
  employerName: "",
  businessName: "",
  mobile: "",
};

function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [showPostForm, setShowPostForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  const user = auth.currentUser;

  // ---------------------------------------
  // LOAD JOBS
  // ---------------------------------------
  const loadJobs = async () => {
    try {
      setLoading(true);

      const snapshot = await getDocs(collection(db, "jobs"));

      const list = snapshot.docs
        .map((jobDoc) => ({
          id: jobDoc.id,
          ...jobDoc.data(),
        }))
        .filter((job) => job.status === "open")
        .sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

      setJobs(list);
    } catch (error) {
      console.error("Error loading jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  // ---------------------------------------
  // FORM CHANGE
  // ---------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ---------------------------------------
  // POST JOB
  // ---------------------------------------
  const handlePostJob = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      alert("Please login to post a job.");
      return;
    }

    if (
      !form.title ||
      !form.category ||
      !form.location ||
      !form.employerName ||
      !form.mobile
    ) {
      alert("Please fill all required fields.");
      return;
    }

    const cleanMobile = form.mobile.replace(/\D/g, "");

    if (cleanMobile.length !== 10) {
      alert("Please enter a valid 10 digit mobile number.");
      return;
    }

    try {
      setPosting(true);

      const jobData = {
        title: form.title.trim(),
        peopleWanted: Number(form.peopleWanted) || 1,
        category: form.category,
        salary: form.salary.trim(),
        jobType: form.jobType,
        location: form.location.trim(),
        description: form.description.trim(),
        workingHours: form.workingHours.trim(),
        employerName: form.employerName.trim(),
        businessName: form.businessName.trim(),
        mobile: `+91${cleanMobile}`,
        uid: auth.currentUser.uid,
        status: "open",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "jobs"), jobData);

      alert("Job posted successfully! 🎉");

      setForm(emptyForm);
      setShowPostForm(false);

      await loadJobs();
    } catch (error) {
      console.error("Error posting job:", error);
      alert("Unable to post job. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  // ---------------------------------------
  // MARK JOB FILLED
  // ---------------------------------------
  const markFilled = async (jobId) => {
    try {
      await updateDoc(doc(db, "jobs", jobId), {
        status: "filled",
        updatedAt: serverTimestamp(),
      });

      setJobs((prev) => prev.filter((job) => job.id !== jobId));
    } catch (error) {
      console.error("Error updating job:", error);
      alert("Unable to update job.");
    }
  };

  // ---------------------------------------
  // DELETE JOB
  // ---------------------------------------
  const deleteJob = async (jobId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this job?"
    );

    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "jobs", jobId));

      setJobs((prev) => prev.filter((job) => job.id !== jobId));
    } catch (error) {
      console.error("Error deleting job:", error);
      alert("Unable to delete job.");
    }
  };

  // ---------------------------------------
  // FILTER JOBS
  // ---------------------------------------
  const filteredJobs = jobs.filter((job) => {
    const text = `
      ${job.title || ""}
      ${job.category || ""}
      ${job.location || ""}
      ${job.description || ""}
      ${job.businessName || ""}
    `.toLowerCase();

    const searchMatch =
      !search || text.includes(search.toLowerCase());

    const categoryMatch =
      !categoryFilter ||
      job.category?.toLowerCase() === categoryFilter.toLowerCase();

    const locationMatch =
      !locationFilter ||
      job.location?.toLowerCase().includes(locationFilter.toLowerCase());

    return searchMatch && categoryMatch && locationMatch;
  });

  const categories = [
    ...new Set(
      jobs
        .map((job) => job.category)
        .filter(Boolean)
    ),
  ];

  // ---------------------------------------
  // RENDER
  // ---------------------------------------
  return (
    <div className="jobs-page">

      {/* HEADER */}
      <div className="jobs-header">
        <div>
          <h1>💼 Jobs</h1>
          <p>Find jobs and people wanted near you</p>
        </div>

        <button
          className="post-job-btn"
          onClick={() => {
            if (!auth.currentUser) {
              alert("Please login to post a job.");
              return;
            }

            setShowPostForm((prev) => !prev);
          }}
        >
          {showPostForm ? "✕ Close" : "＋ Post a Job"}
        </button>
      </div>

      {/* POST JOB FORM */}
      {showPostForm && (
        <div className="job-form-card">
          <h2>Post a Job</h2>
          <p className="form-subtitle">
            Tell people what kind of worker you are looking for.
          </p>

          <form onSubmit={handlePostJob}>

            <label>
              Job Title *
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Eg. Delivery Boy Wanted"
                required
              />
            </label>

            <div className="form-row">

              <label>
                People Wanted *
                <input
                  type="number"
                  name="peopleWanted"
                  min="1"
                  value={form.peopleWanted}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                Category *
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select category</option>
                  <option value="Delivery">Delivery</option>
                  <option value="Driver">Driver</option>
                  <option value="Shop">Shop</option>
                  <option value="Hotel">Hotel</option>
                  <option value="Construction">Construction</option>
                  <option value="Office">Office</option>
                  <option value="Sales">Sales</option>
                  <option value="Technician">Technician</option>
                  <option value="Housekeeping">Housekeeping</option>
                  <option value="Other">Other</option>
                </select>
              </label>

            </div>

            <div className="form-row">

              <label>
                Salary
                <input
                  name="salary"
                  value={form.salary}
                  onChange={handleChange}
                  placeholder="Eg. ₹12000 - ₹15000 / month"
                />
              </label>

              <label>
                Job Type
                <select
                  name="jobType"
                  value={form.jobType}
                  onChange={handleChange}
                >
                  <option>Full Time</option>
                  <option>Part Time</option>
                  <option>Daily Wage</option>
                  <option>Contract</option>
                </select>
              </label>

            </div>

            <label>
              Location *
              <input
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="Eg. Sankagiri"
                required
              />
            </label>

            <label>
              Working Hours
              <input
                name="workingHours"
                value={form.workingHours}
                onChange={handleChange}
                placeholder="Eg. 9 AM - 7 PM"
              />
            </label>

            <label>
              Job Description
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Describe the job..."
                rows="4"
              />
            </label>

            <label>
              Employer Name *
              <input
                name="employerName"
                value={form.employerName}
                onChange={handleChange}
                placeholder="Your name"
                required
              />
            </label>

            <label>
              Business / Shop Name
              <input
                name="businessName"
                value={form.businessName}
                onChange={handleChange}
                placeholder="Business name"
              />
            </label>

            <label>
              Contact Mobile *
              <input
                type="tel"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                placeholder="10 digit mobile number"
                maxLength="10"
                required
              />
            </label>

            <button
              type="submit"
              className="submit-job-btn"
              disabled={posting}
            >
              {posting ? "Posting..." : "🚀 Post Job"}
            </button>

          </form>
        </div>
      )}

      {/* SEARCH */}
      <div className="job-filters">

        <input
          type="text"
          placeholder="🔍 Search jobs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>

          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="📍 Location"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
        />

      </div>

      {/* JOB LIST */}
      <div className="jobs-list">

        {loading ? (
          <div className="jobs-message">
            Loading jobs...
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="jobs-empty">
            <div className="empty-icon">💼</div>
            <h3>No jobs found</h3>
            <p>Be the first employer to post a job.</p>

            {!showPostForm && (
              <button
                className="post-job-btn"
                onClick={() => {
                  if (!auth.currentUser) {
                    alert("Please login to post a job.");
                    return;
                  }

                  setShowPostForm(true);
                }}
              >
                ＋ Post a Job
              </button>
            )}
          </div>
        ) : (
          filteredJobs.map((job) => {

            const isOwner =
              auth.currentUser &&
              job.uid === auth.currentUser.uid;

            return (
              <div className="job-card" key={job.id}>

                <div className="job-card-top">

                  <div>
                    <h2>{job.title}</h2>

                    {job.businessName && (
                      <div className="job-business">
                        🏪 {job.businessName}
                      </div>
                    )}
                  </div>

                  <span className="job-type">
                    {job.jobType}
                  </span>

                </div>

                <div className="job-info">

                  <span>👥 {job.peopleWanted} wanted</span>

                  {job.salary && (
                    <span>💰 {job.salary}</span>
                  )}

                  <span>📍 {job.location}</span>

                  {job.category && (
                    <span>🏷️ {job.category}</span>
                  )}

                  {job.workingHours && (
                    <span>🕐 {job.workingHours}</span>
                  )}

                </div>

                {job.description && (
                  <p className="job-description">
                    {job.description}
                  </p>
                )}

                <div className="job-employer">
                  Posted by <strong>{job.employerName}</strong>
                </div>

                <div className="job-actions">

                  <a
                    className="call-job-btn"
                    href={`tel:${job.mobile}`}
                  >
                    📞 Call Employer
                  </a>

                  {isOwner && (
                    <>
                      <button
                        className="filled-btn"
                        onClick={() => markFilled(job.id)}
                      >
                        ✓ Mark Filled
                      </button>

                      <button
                        className="delete-job-btn"
                        onClick={() => deleteJob(job.id)}
                      >
                        🗑️
                      </button>
                    </>
                  )}

                </div>

              </div>
            );
          })
        )}

      </div>

    </div>
  );
}

export default Jobs;